import React from 'react';
import utils from '../utils/index';
import Popup from '../popup';
import CascaderPanel from '../cascader-panel';
import InputSelect from '../input/input-select';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import Component from '../utils/component';
import PropTypes from 'prop-types';
import Icon from '../icon';

export default class TenCascader extends Component {
  static propTypes = {
    /**
     * 当前值，对应事件 change
     */
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
    /**
     * @member value | data
     */
    valueType: PropTypes.string,
    /**
     * 非受控时，可传入默认值
     */
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object, PropTypes.array]),
    /**
     * 自定义选中值的显示，参数为选中值对应的 option 数组
     */
    customDisplayValue: PropTypes.func,
    /**
     * 输入提示
     */
    placeholder: PropTypes.string,
    /**
     * 大小
     * @defaultValue default
     * @member default|small
     */
    size: PropTypes.string,
    /**
     * 后缀图标
     * @defaultValue carret_down
     */
    suffixIcon: PropTypes.string,
    /**
     * 单选模式，是否可以清空
     */
    clearable: PropTypes.bool,
    /**
     * 禁用
     */
    disabled: PropTypes.bool,
    /**
     * 只读
     */
    readonly: PropTypes.bool,
    /**
     * 筛选结果分割符
     */
    separator: PropTypes.string,
    /** r
     * 选项数据源
     */
    options: PropTypes.array,
    /**
     * 选项 title，自定义每个选项列表的标题
     */
    optionsTitle: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    /**
     * 是否多选
     */
    multiple: PropTypes.bool,
    /**
     * 每个选项都独立可选，而非只有叶子节点可选
     */
    independent: PropTypes.bool,
    /**
     * 指定选项的 value 在 data 中的键值
     */
    optionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 指定选项的 label 在 data 中的键值
     */
    optionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 指定选项的 children 在 data 中的键值
     */
    optionChildren: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 自定义渲染 option 函数，不适用于筛选节点
     */
    renderOption: PropTypes.func,
    /**
     * 自定义加载子节点方法，需设置需要动态加载的节点 children 为 true
     * (data, dataPath) => Promise
     */
    loadChildren: PropTypes.func,
    /**
     * 是否根据选项自动加载子节点（比如初始化 value 有值的时候）
     */
    autoLoadChildren: PropTypes.bool,
    /**
     * 子菜单的展开方式
     * @member click | hover
     */
    expandTrigger: PropTypes.string,
    /**
     * @ignore
     */
    filterText: PropTypes.string,
    /**
     * @ignore
     */
    filterOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    /**
     * 仅子节点可选（一般在 multiple 模式下使用）
     */
    onlyLeafSelectable: PropTypes.bool,
    /**
     * 结果为空时提示文字，支持同名slot
     */
    emptyTip: PropTypes.string,
  };
  static defaultProps = {
    valueType: 'value',
    placeholder: '请选择',
    size: 'default',
    suffixIcon: 'carret_down',
    separator: '/',
    optionValue: 'value',
    optionLabel: 'label',
    optionChildren: 'children',
    autoLoadChildren: true,
    expandTrigger: 'click',
    filterText: '',
    emptyTip: '暂无数据',
  };
  static getDerivedStateFromProps(nextProps) {
    const { value: propValue, optionValue, multiple, valueType } = nextProps;
    const newState = {};

    const transformValue = (v) => {
      let tValue;
      if (!multiple) {
        tValue = !utils.isEmpty(v) ? [v] : [];
      } else {
        tValue = Array.isArray(v) ? v : [];
      }
      tValue = tValue.filter((item) => Array.isArray(item));
      newState.dataPaths = [];
      if (valueType === 'data') {
        tValue = tValue
          .filter((dataPath) =>
            dataPath.every((data) => typeof data === 'object' && typeof data[optionValue] !== 'undefined'),
          )
          .map((dataPath) => {
            newState.dataPaths.push(dataPath);
            return dataPath.map((data) => data[optionValue]);
          });
      }
      return tValue;
    };

    newState.dataValue = transformValue(propValue);

    return newState;
  }

  static Panel = CascaderPanel;

  constructor(props) {
    super(props);
    this.state = {
      popupShow: false,
      filterValue: '',
      dataValue: [],
      dataPaths: [],
    };
  }

  componentDidMount() {
    this.setState({
      disableEmit: false,
    });
  }

  reverseValue(v) {
    const { valueType, multiple } = this.props;
    let reversedValue;

    if (valueType === 'data') {
      reversedValue = v.map((path) => this.getDataPathByPath(path)).filter((item) => item);
    } else {
      reversedValue = v;
    }

    if (!multiple) {
      reversedValue = reversedValue.length > 0 ? reversedValue[0] : null;
    }

    return reversedValue;
  }

  getDataPathByPath(path) {
    const { options, optionChildren, optionValue } = this.props;
    const dataPath = [];
    let children = options;

    for (let i = 0; i < path.length && Array.isArray(children); i++) {
      const key = path[i];
      const foundData = children.find((child) => child[optionValue] === key);

      if (foundData) {
        dataPath.push(foundData);
        children = foundData[optionChildren];
      }
    }

    if (dataPath.length === path.length) {
      return dataPath;
    }

    const foundDataPath = dataPath.find((dPath) =>
      isEqual(path, Array.isArray(dPath) ? dPath.map((data) => data[optionValue]) : [dPath[optionValue]]),
    );
    return foundDataPath;
  }

  getInputSelectText = (path) => {
    const dataPath = this.getDataPathByPath(path);
    const { optionLabel, separator, customDisplayValue } = this.props;
    if (!dataPath) {
      return '';
    }

    if (customDisplayValue) {
      return customDisplayValue(dataPath);
    }

    return dataPath.map((data) => data[optionLabel]).join(separator);
  };

  getInput(el) {
    this.setState({
      refInput: el,
    });
  }

  trigger() {
    this.onInputFocus();
    setTimeout(this.onTriggerClick);
  }

  updateValue(unreversedValue) {
    const reversedValue = this.reverseValue(unreversedValue);
    /**
     * value 值改变事件
     *
     * @event change
     * @param value
     */
    this.props.onChange && this.props.onChange(reversedValue);
  }

  onChange = (value) => {
    const { filterOption, multiple } = this.props;
    if (filterOption) {
      if (!multiple) {
        setTimeout(() => {
          this.setState({
            filterValue: '',
          });
        }, 300);
      } else {
        setTimeout(() => {
          this.setState({
            filterValue: '',
          });
          // this.$refs.input && this.$refs.input.clearText();
        }, 0);
      }
    }

    if (!multiple) {
      this.onPopupHide();
    }

    this.updateValue(multiple ? value : [value]);
    this.onInputFocus();
  };

  onTagDelete = (key) => {
    const newValue = this.state.dataValue.filter((v) => v !== key);
    this.onInputFocus();
    this.updateValue(newValue);
  };

  onFilter = (text) => {
    this.setState({
      filterValue: text.trim(),
    });
    /**
     * @ignore
     * 正在输入筛选值
     */
  };

  onTriggerClick = () => {
    const { disabled } = this.props;
    const filterable = this.isFilterable();

    if (disabled) {
      return;
    }
    this.setState({
      popupShow: filterable ? true : !this.state.popupShow,
    });
  };

  onSuffixIconClick() {
    if (this.readonly || this.disabled) return;
    this.setState({
      popupShow: !this.state.popupShow,
    });
  }

  onClear = () => {
    this.updateValue([]);
  };

  onInputFocus = (event) => {
    const { onFocus } = this.props;
    if (typeof onFocus === 'function') {
      onFocus(event);
    }
  };

  onInputBlur = (event) => {
    const { onBlur } = this.props;
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
    requestAnimationFrame(() => {
      this.context.formBlur && this.context.formBlur();
    });
  };

  onPopupChange() {
    this.hidePopup();
  }

  onPopupHide = () => {
    this.refInputComponent.clearFilter();
    this.setStateSafely({ popupShow: false, filterValue: '' });
    setTimeout(() => this.refInputComponent.blurInput(), 1);
  };

  onPopupShow = () => {
    this.setState({ popupShow: true });
  };

  isFilterable() {
    const { onFilter, filterOption } = this.props;
    return onFilter || filterOption;
  }
  renderInput() {
    const { dataValue, popupShow, filterValue } = this.state;
    const {
      readonly,
      size,
      multiple,
      className: propClassName,
      disabled,
      suffixIcon,
      clearable,
      collapseTags,
      defaultValue,
      placeholder,
    } = this.props;

    const className = classNames(propClassName, 'ten-select  ten-select-input', {
      'ten-select-input--popupshow': popupShow,
    });

    const readOnly = readonly || !popupShow;

    const filterable = this.isFilterable();
    const _suffixIcon = <Icon type={suffixIcon || 'carret_down'} onClick={this.onCarretClick} />;

    const inputDisabled = disabled === true;

    return (
      <InputSelect
        ref={(c) => {
          this.refInputComponent = c;
        }}
        readOnly={readOnly}
        multiple={multiple}
        size={size}
        className={className}
        value={dataValue}
        filterValue={filterValue}
        onFilter={filterable ? this.onFilter : undefined}
        onClick={this.onTriggerClick}
        getSelectedText={this.getInputSelectText}
        onTagDelete={this.onTagDelete}
        disabled={inputDisabled}
        suffixIcon={_suffixIcon}
        onFocus={this.onInputFocus}
        onBlur={this.onInputBlur}
        onClear={clearable ? this.onClear : undefined}
        collapseTags={collapseTags}
        defaultValue={defaultValue}
        placeholder={placeholder}
      />
    );
  }

  renderCascader = () => {
    const {
      valueType,
      defaultValue,
      customDisplayValue,
      placeholder,
      size,
      suffixIcon,
      clearable,
      disabled,
      readonly,
      ...cascaderProps
    } = this.props;
    return <CascaderPanel {...cascaderProps} onChange={this.onChange} filterText={this.state.filterValue} />;
  };

  render() {
    const { popupShow } = this.state;
    const className = classNames([
      'ten-cascader__popup',
      {
        'ten-cascader__popup--loading': this.props.loading,
      },
    ]);

    return (
      <Popup
        className={className}
        content={this.renderCascader()}
        show={popupShow}
        direction="bottom left"
        triggerType="manual"
        onHide={this.onPopupHide}
        onShow={this.onPopupShow}
      >
        {this.renderInput()}
      </Popup>
    );
  }
}
